import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Bell, Plus, Play, Trash2, Send, Users, Zap,
  Clock, Star, Heart, CalendarDays, MessageCircle, Megaphone,
  ArrowLeft, ChevronRight, Target, TestTube, Sparkles, Image,
  Loader2, Wand2, AlertTriangle, XCircle, MinusCircle, Mail, Smartphone, Save, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config/api';

// Campaigns are stored and really sent by notification-service: SMS (SignalWire)
// or e-mail. There is no push provider, so push / app channels are not offered.
// Sending is one-time "send now"; scheduled / recurring / trigger-based sending
// does not exist in the service (it answers 501), so it is not offered here.

const TEMPLATE_ICONS = {
  'heart': Heart, 'clock': Clock, 'star': Star, 'zap': Zap,
  'utensils': Megaphone, 'calendar': CalendarDays,
  'alarm-clock': Clock, 'message-circle': MessageCircle,
};

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  sending: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const CHANNELS = { sms: 'SMS', email: 'E-mail' };
const CHANNEL_ICONS = { sms: Smartphone, email: Mail };

const SCHEDULE_LABELS = {
  one_time: 'One-time (send now)', recurring_daily: 'Daily', recurring_weekly: 'Weekly', trigger_based: 'Trigger-based',
};

const POLL_MS = 3000;

// {promo_code}-style blanks the owner must fill before a campaign can be sent.
const PLACEHOLDER_RE = /\{[a-zA-Z_]+\}/g;
function unfilledPlaceholders(...texts) {
  const found = new Set();
  texts.forEach(t => (String(t || '').match(PLACEHOLDER_RE) || []).forEach(p => found.add(p)));
  return [...found].sort();
}

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
  if (d) return JSON.stringify(d);
  if (e?.response?.status) return `${fallback} (HTTP ${e.response.status})`;
  return `${fallback}: ${e?.message || 'network error'}`;
}

// Toast the server's own reason; 501 / 503 are "not available / not configured".
function showError(e, fallback) {
  const status = e?.response?.status;
  const msg = errText(e, fallback);
  if (status === 501) toast.error(`Not available: ${msg}`, { duration: 9000 });
  else if (status === 503) toast.error(`Not configured: ${msg}`, { duration: 9000 });
  else toast.error(msg, { duration: 7000 });
}

function sendable(c) {
  return c.schedule_type === 'one_time' && !c.scheduled_at;
}

export default function NotificationCampaignsPage() {
  const { token, restaurant } = useOutletContext() || {};
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState({});
  const [templates, setTemplates] = useState([]);
  const [audiences, setAudiences] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [testFor, setTestFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const sendingIds = useRef(new Set());

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const [campRes, tmplRes, audRes] = await Promise.all([
        axios.get(`${API}/notification-campaigns${statusFilter ? `?status=${statusFilter}` : ''}`, { headers }),
        axios.get(`${API}/notification-campaigns/templates`, { headers }),
        axios.get(`${API}/notification-campaigns/audiences`, { headers }),
      ]);
      const list = campRes.data.campaigns || [];
      // A send that just finished: say what the sender answered.
      list.forEach(c => {
        if (sendingIds.current.has(c.id) && c.status !== 'sending') {
          sendingIds.current.delete(c.id);
          const s = c.stats || {};
          const line = `"${c.name}": sent ${s.sent || 0}, failed ${s.failed || 0}, skipped ${s.skipped || 0}`;
          if (c.status === 'failed') toast.error(`Send failed - ${line}${c.last_send_result?.error ? ` (${c.last_send_result.error})` : ''}`, { duration: 10000 });
          else toast.success(`Send finished - ${line}`, { duration: 8000 });
        }
        if (c.status === 'sending') sendingIds.current.add(c.id);
      });
      setCampaigns(list);
      setSummary(campRes.data.summary || {});
      setTemplates(tmplRes.data.templates || []);
      setAudiences(audRes.data.audiences || {});
      setLoadError('');
    } catch (e) {
      setLoadError(errText(e, 'Failed to load campaigns'));
    }
    if (!quiet) setLoading(false);
  }, [statusFilter, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // While anything is sending, refresh for the sender's real counts.
  const anySending = campaigns.some(c => c.status === 'sending') || sendingIds.current.size > 0;
  useEffect(() => {
    if (!anySending) return undefined;
    const t = setTimeout(() => load({ quiet: true }), POLL_MS);
    return () => clearTimeout(t);
  }, [anySending, campaigns, load]);

  const activateCampaign = async (c) => {
    const missing = unfilledPlaceholders(c.notification_title, c.notification_body);
    if (missing.length) {
      toast.error(`Fill in ${missing.join(', ')} before sending`);
      setDetailId(c.id);
      return;
    }
    if (!window.confirm(`Send "${c.name}" by ${CHANNELS[c.channel] || c.channel} to ${audiences[c.target_audience]?.name || c.target_audience} now?`)) return;
    try {
      const { data } = await axios.post(`${API}/notification-campaigns/${c.id}/activate`, {}, { headers });
      sendingIds.current.add(c.id);
      toast.info(data.message || 'Sending...', { duration: 7000 });
      load({ quiet: true });
    } catch (e) { showError(e, 'Activation failed'); load({ quiet: true }); }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/notification-campaigns/${c.id}`, { headers });
      toast.success('Campaign deleted');
      if (detailId === c.id) setDetailId(null);
      load({ quiet: true });
    } catch (e) { showError(e, 'Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-xl mx-auto mt-12 text-center border border-red-200 bg-red-50 dark:bg-red-900/20 rounded-xl p-6" data-testid="camp-load-error">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="font-semibold text-red-700">Campaigns could not be loaded</p>
        <p className="text-sm text-red-600 mt-1">{loadError}</p>
        <Button size="sm" variant="outline" className="mt-4" onClick={() => load()}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
        </Button>
      </div>
    );
  }

  const totals = campaigns.reduce((acc, c) => {
    const s = c.stats || {};
    acc.failed += s.failed || 0; acc.skipped += s.skipped || 0;
    return acc;
  }, { failed: 0, skipped: 0 });
  const detail = campaigns.find(c => c.id === detailId);

  return (
    <div className="space-y-6" data-testid="notification-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-blue-600" /> Campaigns
          </h1>
          <p className="text-gray-500 text-sm mt-1">Send SMS or e-mail campaigns to your customers or staff</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => load({ quiet: true })} data-testid="camp-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}
            className="bg-blue-600 hover:bg-blue-700" data-testid="camp-create-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Summary: the sender's real answers. Opens / clicks are not tracked. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Megaphone} label="Sending" value={summary.active_campaigns || 0} color="text-sky-600" bg="bg-sky-50 dark:bg-sky-900/20" />
        <StatCard icon={Send} label="Total Sent" value={summary.total_sent || 0} color="text-blue-600" bg="bg-blue-50 dark:bg-blue-900/20" />
        <StatCard icon={XCircle} label="Failed" value={totals.failed} color="text-red-600" bg="bg-red-50 dark:bg-red-900/20" />
        <StatCard icon={MinusCircle} label="Skipped" value={totals.skipped} color="text-amber-600" bg="bg-amber-50 dark:bg-amber-900/20"
          hint="No phone / e-mail on file, or no consent" />
      </div>
      <p className="text-xs text-gray-400 -mt-3">Sent / failed / skipped are the SMS and e-mail provider's own answers. Opens and clicks are not tracked.</p>

      {/* Filter */}
      <div className="flex gap-1.5 flex-wrap">
        {['', 'draft', 'sending', 'completed', 'failed', 'paused'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
            }`} data-testid={`camp-filter-${s || 'all'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Campaign List */}
      <div className="space-y-3">
        {campaigns.length === 0 && (
          <div className="text-center py-12 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            <Megaphone className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No campaigns yet</p>
            <p className="text-gray-400 text-sm mt-1">Create your first SMS or e-mail campaign</p>
          </div>
        )}
        {campaigns.map(c => (
          <CampaignCard key={c.id} campaign={c}
            onActivate={() => activateCampaign(c)}
            onDelete={() => deleteCampaign(c)} onSendTest={() => setTestFor(c)}
            onView={() => setDetailId(c.id)} />
        ))}
      </div>

      {showCreate && (
        <CreateCampaignDialog headers={headers}
          open={showCreate}
          onClose={() => setShowCreate(false)}
          templates={templates}
          audiences={audiences}
          restaurantName={restaurant?.name || ''}
          onCreated={(created) => { setShowCreate(false); load({ quiet: true }); if (created?.id) setDetailId(created.id); }}
        />
      )}

      {detail && (
        <CampaignDetailDialog
          campaign={detail}
          audiences={audiences}
          headers={headers}
          onClose={() => setDetailId(null)}
          onSaved={() => load({ quiet: true })}
          onActivate={() => activateCampaign(detail)}
          onSendTest={() => setTestFor(detail)}
        />
      )}

      {testFor && (
        <SendTestDialog campaign={testFor} headers={headers} onClose={() => setTestFor(null)} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg, hint }) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50`} title={hint || ''}
      data-testid={`camp-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
    </div>
  );
}

function PlaceholderWarning({ missing }) {
  if (!missing.length) return null;
  return (
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg p-3 text-sm" data-testid="camp-placeholder-warning">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold">Fill in the blanks before sending</p>
        <p className="text-xs mt-0.5">
          Replace {missing.map(p => <code key={p} className="bg-amber-100 px-1 rounded mx-0.5">{p}</code>)} with your real
          offer, code, time or link. A campaign with blanks cannot be sent or tested.
        </p>
      </div>
    </div>
  );
}

function CampaignCard({ campaign: c, onActivate, onDelete, onSendTest, onView }) {
  const stats = c.stats || {};
  const missing = unfilledPlaceholders(c.notification_title, c.notification_body);
  const ChannelIcon = CHANNEL_ICONS[c.channel] || Bell;
  const canSend = ['draft', 'paused', 'failed', 'completed'].includes(c.status) && !missing.length && sendable(c);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
      data-testid={`camp-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onView}>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{c.name}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>
              {c.status === 'sending' ? <><Loader2 className="w-2.5 h-2.5 inline animate-spin mr-0.5" />sending</> : c.status}
            </span>
            {missing.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800" data-testid={`camp-blanks-${c.id}`}>
                {missing.length} blank{missing.length > 1 ? 's' : ''} to fill
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{c.notification_title}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1"><Target className="w-3 h-3" />{c.target_audience?.replace(/_/g, ' ')}</span>
            <span className="flex items-center gap-1"><ChannelIcon className="w-3 h-3" />{CHANNELS[c.channel] || `${c.channel} (not deliverable)`}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{SCHEDULE_LABELS[c.schedule_type] || c.schedule_type}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {(stats.sent > 0 || stats.failed > 0 || stats.skipped > 0) && (
            <div className="text-right hidden sm:flex items-center gap-3 text-xs">
              <div><span className="text-gray-400">Sent</span> <span className="font-semibold text-gray-700 dark:text-gray-300 ml-1">{stats.sent || 0}</span></div>
              <div><span className="text-gray-400">Failed</span> <span className={`font-semibold ml-1 ${stats.failed ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{stats.failed || 0}</span></div>
              <div><span className="text-gray-400">Skipped</span> <span className="font-semibold text-gray-700 dark:text-gray-300 ml-1">{stats.skipped || 0}</span></div>
            </div>
          )}
          <div className="flex gap-1">
            {c.status !== 'sending' && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onActivate} disabled={!canSend}
                title={missing.length ? `Fill in ${missing.join(', ')} first` : (!sendable(c) ? 'Only one-time campaigns can be sent' : 'Send now')}
                data-testid={`camp-activate-${c.id}`}>
                <Play className="w-3.5 h-3.5 text-emerald-600" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onSendTest} disabled={missing.length > 0}
              title={missing.length ? `Fill in ${missing.join(', ')} first` : 'Send a test to one phone / e-mail'}
              data-testid={`camp-test-${c.id}`}>
              <TestTube className="w-3.5 h-3.5 text-blue-500" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDelete} disabled={c.status === 'sending'} title="Delete" data-testid={`camp-delete-${c.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AudienceSelect({ value, onChange, audiences, channel }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full mt-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
      data-testid="camp-form-audience">
      {Object.entries(audiences).map(([k, v]) => (
        <option key={k} value={k}>
          {v.name} ({channel === 'email' ? v.reachable_email : v.reachable_sms} reachable of {v.estimated})
        </option>
      ))}
    </select>
  );
}

function ChannelSelect({ value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full mt-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
      data-testid="camp-form-channel">
      {Object.entries(CHANNELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
}

function AudienceNote({ audience, channel }) {
  if (!audience) return null;
  const reachable = channel === 'email' ? audience.reachable_email : audience.reachable_sms;
  return (
    <div className="bg-blue-50 rounded-lg p-3 text-sm">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-blue-600" />
        <span className="font-medium text-blue-900">{reachable} of {audience.estimated} can be reached by {CHANNELS[channel] || channel}</span>
      </div>
      <p className="text-blue-700 text-xs mt-1">
        {audience.description}. {channel === 'email'
          ? 'Customers need an e-mail on file and promotional e-mail left on.'
          : 'Customers need a phone on file and SMS notifications turned on.'}
      </p>
    </div>
  );
}

const BLANK_FORM = {
  name: '', notification_title: '', notification_body: '',
  target_audience: 'all_customers', channel: 'sms',
  schedule_type: 'one_time', action_url: '',
};

function CreateCampaignDialog({ open, onClose, templates, audiences, restaurantName, headers, onCreated }) {
  const [step, setStep] = useState('template'); // template | customize
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [aiTone, setAiTone] = useState('friendly');

  const missing = unfilledPlaceholders(form.notification_title, form.notification_body);

  const selectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setForm({
      name: tmpl.name,
      notification_title: tmpl.default_title,
      notification_body: tmpl.default_body,
      target_audience: tmpl.target_audience,
      channel: CHANNELS[tmpl.channel] ? tmpl.channel : 'sms',
      schedule_type: 'one_time',  // the only kind the service can send
      action_url: '',
    });
    setStep('customize');
  };

  const startBlank = () => {
    setSelectedTemplate(null);
    setForm(BLANK_FORM);
    setStep('customize');
  };

  const handleAiContent = async () => {
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/campaign-ai/generate-content`, {
        campaign_type: selectedTemplate?.id || form.name.toLowerCase().replace(/\s/g, '_') || 'promotion',
        ...(restaurantName ? { restaurant_name: restaurantName } : {}),
        tone: aiTone,
        promo_details: form.notification_body || null,
        target_audience: form.target_audience?.replace(/_/g, ' '),
      }, { headers });
      if (data.success && data.content) {
        setForm(f => ({
          ...f,
          notification_title: data.content.title || f.notification_title,
          notification_body: (f.channel === 'sms' && data.content.sms_text) || data.content.body || f.notification_body,
        }));
        toast.success('AI content written - check it and fill any blanks');
      }
    } catch (e) {
      showError(e, 'AI generation failed');
    }
    setAiLoading(false);
  };

  const handleAiImage = async () => {
    setImgLoading(true);
    try {
      const { data } = await axios.post(`${API}/campaign-ai/generate-image`, {
        campaign_type: selectedTemplate?.id || 'flash_sale',
        description: form.notification_body || form.name,
        ...(restaurantName ? { restaurant_name: restaurantName } : {}),
        style: 'modern',
      }, { headers });
      if (data.success && data.image_base64) {
        setGeneratedImage(`data:image/png;base64,${data.image_base64}`);
        toast.success('Image generated');
      }
    } catch (e) {
      showError(e, 'Image generation failed');
    }
    setImgLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.notification_title.trim() || !form.notification_body.trim()) {
      toast.error('Name, title and message are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/notification-campaigns`, {
        ...form,
        action_url: form.action_url.trim() || null,
        template_id: selectedTemplate?.id || null,
      }, { headers });
      toast.success(missing.length ? `Saved as draft - fill in ${missing.join(', ')} before sending` : 'Campaign saved as draft');
      onCreated(data.campaign);
    } catch (e) { showError(e, 'Failed to create campaign'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'customize' && (
              <button onClick={() => setStep('template')} className="text-gray-400 hover:text-gray-600">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <Megaphone className="w-5 h-5 text-blue-600" />
            {step === 'template' ? 'Choose a Template' : 'Customize Campaign'}
          </DialogTitle>
        </DialogHeader>

        {step === 'template' && (
          <div className="space-y-3">
            <button onClick={startBlank}
              className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 transition-colors text-left"
              data-testid="camp-tmpl-blank">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Start from Scratch</p>
                  <p className="text-xs text-gray-500">Write your own message</p>
                </div>
              </div>
            </button>
            {templates.map(t => {
              const Icon = TEMPLATE_ICONS[t.icon] || Bell;
              const blanks = unfilledPlaceholders(t.default_title, t.default_body);
              return (
                <button key={t.id} onClick={() => selectTemplate(t)}
                  className="w-full p-4 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 transition-colors text-left"
                  data-testid={`camp-tmpl-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{t.name}</p>
                        <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{t.category}</span>
                        {blanks.length > 0 && (
                          <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">you fill: {blanks.join(' ')}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {step === 'customize' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Campaign Name</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Spring Win-Back" className="mt-1" data-testid="camp-form-name" />
            </div>

            {/* AI Content Generator */}
            <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-violet-900 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-violet-600" /> AI Content Assistant
                </p>
                <select value={aiTone} onChange={e => setAiTone(e.target.value)}
                  className="text-xs bg-white border border-violet-200 rounded-lg px-2 py-1 text-violet-700"
                  data-testid="camp-ai-tone">
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent</option>
                  <option value="playful">Playful</option>
                  <option value="professional">Professional</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAiContent} disabled={aiLoading}
                  className="bg-violet-600 hover:bg-violet-700 text-white flex-1" data-testid="camp-ai-write">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  {aiLoading ? 'Writing...' : 'Write Content'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleAiImage} disabled={imgLoading}
                  className="border-violet-300 text-violet-700 hover:bg-violet-100 flex-1" data-testid="camp-ai-image">
                  {imgLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Image className="w-3.5 h-3.5 mr-1" />}
                  {imgLoading ? 'Creating...' : 'Generate Image'}
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <div className="mt-1 border border-gray-200 rounded-xl p-3 bg-gray-50">
                <Input value={form.notification_title}
                  onChange={e => setForm({ ...form, notification_title: e.target.value })}
                  placeholder={form.channel === 'email' ? 'E-mail subject' : 'Title (starts the text)'}
                  className="mb-2 font-semibold" data-testid="camp-form-title" />
                <textarea value={form.notification_body}
                  onChange={e => setForm({ ...form, notification_body: e.target.value })}
                  placeholder="Message..."
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none h-24 outline-none focus:border-blue-300"
                  data-testid="camp-form-body" />
                {form.channel === 'sms' && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    Sent as: "{restaurantName ? `${restaurantName}: ` : ''}title - message{form.action_url ? ' link' : ''}"; customer audiences also get "Reply STOP to opt out."
                  </p>
                )}
              </div>
            </div>

            <PlaceholderWarning missing={missing} />

            {generatedImage && (
              <div data-testid="camp-generated-image">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Generated image</label>
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  <img src={generatedImage} alt="Campaign" className="w-full h-48 object-cover" />
                  <button onClick={() => setGeneratedImage(null)}
                    className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-black/70">
                    x
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  The image is not attached to the SMS / e-mail. <a href={generatedImage} download="campaign-image.png" className="text-blue-600 underline">Download it</a> to use on your menu or social pages.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Audience</label>
                <AudienceSelect value={form.target_audience} audiences={audiences} channel={form.channel}
                  onChange={v => setForm({ ...form, target_audience: v })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Send by</label>
                <ChannelSelect value={form.channel} onChange={v => setForm({ ...form, channel: v })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">When</label>
                <select value="one_time" disabled
                  className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
                  data-testid="camp-form-schedule">
                  <option value="one_time">{SCHEDULE_LABELS.one_time}</option>
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Scheduled and recurring sends are not available yet.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Link (optional)</label>
                <Input value={form.action_url}
                  onChange={e => setForm({ ...form, action_url: e.target.value })}
                  placeholder="https://..." className="mt-1" data-testid="camp-form-url" />
              </div>
            </div>

            <AudienceNote audience={audiences[form.target_audience]} channel={form.channel} />

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={handleCreate} disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="camp-form-submit">
                {saving ? 'Saving...' : 'Save as Draft'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CampaignDetailDialog({ campaign: c, audiences, headers, onClose, onSaved, onActivate, onSendTest }) {
  const [form, setForm] = useState(() => ({
    name: c.name, notification_title: c.notification_title, notification_body: c.notification_body,
    channel: CHANNELS[c.channel] ? c.channel : 'sms', target_audience: c.target_audience, action_url: c.action_url || '',
  }));
  const [saving, setSaving] = useState(false);
  const stats = c.stats || {};
  const result = c.last_send_result || {};
  const editable = c.status !== 'sending';
  const dirty = form.name !== c.name || form.notification_title !== c.notification_title
    || form.notification_body !== c.notification_body || form.channel !== c.channel
    || form.target_audience !== c.target_audience || (form.action_url || '') !== (c.action_url || '');
  const missing = unfilledPlaceholders(form.notification_title, form.notification_body);
  const savedMissing = unfilledPlaceholders(c.notification_title, c.notification_body);
  const canSend = editable && !dirty && !savedMissing.length && sendable(c);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-campaigns/${c.id}`, {
        ...form, action_url: form.action_url.trim() || null,
      }, { headers });
      toast.success('Campaign saved');
      onSaved();
    } catch (e) { showError(e, 'Failed to save'); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-blue-600" /> {c.name}
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ml-auto ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>
              {c.status}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4" data-testid="camp-detail">
          {c.status === 'sending' && (
            <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-50 rounded-lg p-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Sending now - the counts below update when the provider has answered.
            </div>
          )}

          {/* Real counts from the last send */}
          {(c.last_sent_at || stats.sent > 0 || stats.failed > 0) && (
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-gray-900">{stats.sent || 0}</p>
                <p className="text-[10px] text-gray-500">Sent</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-red-600">{stats.failed || 0}</p>
                <p className="text-[10px] text-gray-500">Failed</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-amber-600">{stats.skipped || 0}</p>
                <p className="text-[10px] text-gray-500">Skipped (no address / consent)</p>
              </div>
            </div>
          )}
          {result.error && (
            <p className="text-xs text-red-600 bg-red-50 rounded p-2" data-testid="camp-detail-error">Last send: {result.error}</p>
          )}
          {Array.isArray(result.errors) && result.errors.length > 0 && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer">Why {result.failed} failed</summary>
              <ul className="mt-1 space-y-0.5">
                {result.errors.map((er, i) => <li key={i}>#{er.recipient_id}: {String(er.error)}</li>)}
              </ul>
            </details>
          )}

          {/* Edit */}
          <div className="space-y-2">
            <Input value={form.name} disabled={!editable} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Campaign name" data-testid="camp-edit-name" />
            <Input value={form.notification_title} disabled={!editable}
              onChange={e => setForm({ ...form, notification_title: e.target.value })}
              placeholder="Title / e-mail subject" className="font-semibold" data-testid="camp-edit-title" />
            <textarea value={form.notification_body} disabled={!editable}
              onChange={e => setForm({ ...form, notification_body: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none h-24 outline-none focus:border-blue-300"
              data-testid="camp-edit-body" />
            <PlaceholderWarning missing={missing} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Audience</label>
                <AudienceSelect value={form.target_audience} audiences={audiences} channel={form.channel}
                  onChange={v => setForm({ ...form, target_audience: v })} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Send by</label>
                <ChannelSelect value={form.channel} onChange={v => setForm({ ...form, channel: v })} />
              </div>
            </div>
            <Input value={form.action_url} disabled={!editable} onChange={e => setForm({ ...form, action_url: e.target.value })}
              placeholder="Link (optional) https://..." data-testid="camp-edit-url" />
            <AudienceNote audience={audiences[form.target_audience]} channel={form.channel} />
            {!sendable(c) && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                This campaign is "{SCHEDULE_LABELS[c.schedule_type] || c.schedule_type}". Only one-time campaigns can be sent; scheduled and recurring sending is not available.
              </p>
            )}
            {!CHANNELS[c.channel] && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                Saved channel "{c.channel}" cannot be delivered (no push provider). Choose SMS or E-mail and save.
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2 flex-wrap">
            {editable && (
              <Button variant="outline" onClick={save} disabled={!dirty || saving} data-testid="camp-detail-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
              </Button>
            )}
            {editable && (
              <Button onClick={onActivate} disabled={!canSend}
                title={dirty ? 'Save your changes first' : (savedMissing.length ? `Fill in ${savedMissing.join(', ')} first` : '')}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="camp-detail-activate">
                <Play className="w-4 h-4 mr-1.5" /> Send now
              </Button>
            )}
            <Button variant="outline" onClick={onSendTest} disabled={dirty || savedMissing.length > 0} data-testid="camp-detail-test">
              <TestTube className="w-4 h-4 mr-1.5" /> Send Test
            </Button>
          </div>
          {dirty && <p className="text-[11px] text-gray-500">Save your changes before sending or testing.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SendTestDialog({ campaign: c, headers, onClose }) {
  const isEmail = c.channel === 'email';
  const [address, setAddress] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const value = address.trim();
    if (!value) { toast.error(isEmail ? 'Enter an e-mail address' : 'Enter a phone number'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/notification-campaigns/${c.id}/send-test`,
        isEmail ? { email: value } : { phone: value }, { headers });
      toast.success(data.message || 'Test sent');
      onClose();
    } catch (e) { showError(e, 'Test send failed'); }
    setSending(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5 text-blue-600" /> Send a test {isEmail ? 'e-mail' : 'SMS'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-500">"{c.name}" is sent once to this {isEmail ? 'address' : 'number'} only.</p>
          <Input autoFocus value={address} onChange={e => setAddress(e.target.value)}
            type={isEmail ? 'email' : 'tel'} placeholder={isEmail ? 'you@example.com' : '+1 555 123 4567'}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            data-testid="camp-test-address" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={send} disabled={sending} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="camp-test-send">
              {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send test
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
